import React from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'
import { useApi } from '@/hooks'
import { type DataSource } from '@/types/data-source'

interface Facility {
  id: number | string
  name: string
}

interface Params {
  companyId?: number
  facility?: string
  onChange: (facilityId: string | undefined) => void
  label?: string
  /** Which back end to list facilities from; defaults to Fresh. */
  dataSource?: DataSource
}

export const FacilityDropdownSingle: React.FC<Params> = ({
  facility,
  companyId,
  onChange,
  label = 'Facility',
  dataSource,
}) => {
  const { api } = useApi()
  const [options, setOptions] = React.useState<DropdownOption[]>([])
  const [placeholder, setPlaceholder] = React.useState('')

  React.useEffect(() => {
    if (!api) return
    if (!companyId) {
      setOptions([])
      setPlaceholder('Choose a company to load facilities...')
      return
    }

    let cancelled = false
    const loadFacilities = async () => {
      setPlaceholder('Loading...')
      const response = await api.get<Facility[]>({
        path: `/fresh/facilities?companyId=${companyId}&skip=0&take=10000${dataSource ? `&dataSource=${dataSource}` : ''}`,
        origin: 'FacilityDropdown',
        expiresIn: '2h',
      })
      if (cancelled) return
      if (response.error || !response.data) {
        console.error(response.error)
        setPlaceholder('Error loading facilities. Contact support.')
        setOptions([])
        return
      }

      setPlaceholder('Choose a facility...')
      setOptions([
        { label: 'All Facilities', value: '' },
        ...response.data.map((item) => ({ label: item.name, value: String(item.id) })),
      ])
    }
    void loadFacilities()

    return () => {
      cancelled = true
    }
  }, [api, companyId, dataSource])

  return (
    <div>
      <label>{label}</label>
      <Dropdown
        options={options}
        handleSingleSelectItem={(option: DropdownOption | null) => onChange(option?.value || undefined)}
        selectedItem={options.find((option) => option.value === (facility ?? '')) || null}
        placeholder={placeholder}
        animateDots={/^Loading/.test(placeholder)}
      />
    </div>
  )
}
